import "./phaser-global"; // DOIT rester en premier : pose le global Phaser avant les scènes.
import "./index.css";
import { GridEngine } from "grid-engine";
import RexUIPlugin from "phaser3-rex-plugins/templates/ui/ui-plugin.js";

import HouseScene from "./Scenes/HouseScene";
import TestScene from "./Scenes/TestScene";
import DialogueScene from "./Scenes/DialogueScene";
import ControlsScene from "./Scenes/ControlsScene";
import HudScene from "./Scenes/HudScene";
import WeatherScene from "./Scenes/WeatherScene";
import LogScene from "./Scenes/LogScene";
import MinimapScene from "./Scenes/MinimapScene";
import RoomScene from "./Scenes/RoomScene";
import ChambresScene from "./Scenes/ChambresScene";
import GreyboxLocationScene from "./Scenes/greybox/GreyboxLocationScene";
import { LOCATIONS } from "./Scenes/greybox/campusMaps";
import { SceneName } from "./Scenes/enums/SceneNames";
import GlobalInfo from "./GlobalInfo";
import ServerLink from "./net/ServerLink";
import { isTouchDevice } from "./utils/touch";

// Enregistrement du Service Worker PWA (généré par vite-plugin-pwa).
import { registerSW } from "virtual:pwa-register";
registerSW({ immediate: true });

// Serveur de monde (society sim) : connexion WebSocket autoritaire. Les agents IA sont pilotés
// par le serveur ; le jeu devient un moteur de rendu + entrées. Surcharge l'URL avec
// VITE_WORLD_SERVER au besoin (ex : ws://192.168.x.x:8787 pour tester sur mobile).
ServerLink.connect((import.meta as any).env?.VITE_WORLD_SERVER);

// Campus LaCaze : une scène greybox par lieu. LOCATIONS[0] = Campus (overworld) => scène de boot.
const campusScenes = LOCATIONS.map((cfg) => new GreyboxLocationScene(cfg));

const config = {
    type: Phaser.AUTO,
    backgroundColor: '#1a202c',
    // Campus en premier (boot). DialogueScene = overlay UI lancé par GameScene.create.
    // HouseScene/TestScene conservées en fin pour référence. (toutes des instances de Scene.)
    scene: [...campusScenes, new RoomScene(), new TestScene(), new HouseScene(), new DialogueScene(), new ControlsScene(), new HudScene(), new WeatherScene(), new LogScene(), new MinimapScene(), new ChambresScene()],
    plugins: {
        scene: [
            {
                key: "gridEngine",
                plugin: GridEngine,
                mapping: "gridEngine",
            },
            {
                key: "rexUI",
                plugin: RexUIPlugin,
                mapping: "rexUI",
            },
        ],
    },
    scale: {
        parent: 'phaser-game',
        // RESIZE : le canvas REMPLIT le conteneur plein écran (#phaser-game = 100vw/100vh) — pas de
        // bandes vides sur mobile, et les contrôles tactiles se calent aux vrais coins de l'écran.
        // Chaque scène lit this.scale.width/height ; la caméra adapte son zoom à la carte (fitCameraZoom).
        mode: Phaser.Scale.RESIZE,
        width: 600,
        height: 600
    },
    physics: {
        default: 'arcade',
        arcade: {
            debug: false
        }
    },
};

const game = new Phaser.Game(config);

// Tous les overlays (dialogue + barre d'outils HUD/météo/Journal/minimap + contrôles tactiles) sont lancés
// UNE fois au boot, AVANT le switch "server-spawn" et indépendamment de la scène de lieu active. Avant, ils
// dépendaient du create() de la 1re scène de jeu (campus) ; si le boot bascule aussitôt vers un autre lieu
// (CDI…), ce couplage au timing pouvait laisser la barre d'outils absente. Lancement découplé = robuste.
// (Controls = joystick mobile, seulement sur écran tactile.) C'est désormais l'UNIQUE site de lancement.
const TOOLKIT_OVERLAYS = [SceneName.Dialogue, SceneName.Hud, SceneName.Weather, SceneName.Log, SceneName.Minimap, SceneName.Chambres];
game.events.once(Phaser.Core.Events.READY, () => {
    for (const key of TOOLKIT_OVERLAYS) {
        if (!game.scene.isActive(key)) game.scene.run(key);
    }
    if (isTouchDevice() && !game.scene.isActive(SceneName.Controls)) game.scene.run(SceneName.Controls);
});

// Le serveur choisit où apparaît Caz (le lieu le plus animé). Le client boote TOUJOURS sur le Campus ;
// au premier snapshot on bascule vers la scène du lieu de spawn (= LocationId = SceneName). On switch
// depuis la scène campus (la seule éveillée au boot), en réessayant tant qu'elle n'est pas prête.
// "Chez moi" — enter a room from the directory: stop the active location scene + start the (single) RoomScene
// retargeted to room:<avatarId> via scene data. scene.start re-inits, so the room's drivers rebuild fresh
// against the new room. Exit ("← Sortir") starts campus again. Overlays (HUD/Chambres/…) persist throughout.
GlobalInfo.on('room-enter', (p: { roomId?: string; ownerName?: string; mine?: boolean; recentVisits?: number }) => {
    if (!p?.roomId) return;
    const active = (GlobalInfo._gameProgress.activeScene as string) || SceneName.Campus;
    if (active !== SceneName.Room && game.scene.isActive(active)) game.scene.stop(active);
    game.scene.start(SceneName.Room, { roomId: p.roomId, ownerName: p.ownerName, mine: p.mine, recentVisits: p.recentVisits });
});

GlobalInfo.once('server-spawn', (p: { location?: string }) => {
    const target = p?.location;
    if (!target || target === SceneName.Campus) return; // déjà au bon endroit
    let tries = 0;
    const trySwitch = (): void => {
        const from = (GlobalInfo._gameProgress.activeScene as string) || SceneName.Campus;
        const fromScene = game.scene.getScene(from) as Phaser.Scene | null;
        if (fromScene && game.scene.isActive(from)) (fromScene as any).scene.switch(target);
        else if (tries++ < 60) setTimeout(trySwitch, 50);
    };
    trySwitch();
});

// Exposé pour le QA / debug (inspecter la scène active dans la console).
(globalThis as any).game = game;
(globalThis as any).GlobalInfo = GlobalInfo;